/**
 * THE LABELGRID CONTRACT — what the distribution API actually accepts and returns.
 *
 * ============================================================================
 * NOTHING HERE ISSUES A REQUEST, AND NOTHING HERE HOLDS A TOKEN. This module
 * builds the documented LabelGrid request bodies and reads the documented
 * LabelGrid responses. Both halves are real, exported and unit tested — but the
 * dispatch is not here and MUST NOT be, because a LabelGrid bearer token in a
 * browser bundle is a LabelGrid bearer token in every visitor's browser.
 * Transport belongs on the server; deployment/runtime blockers belong in
 * src/api/labelgrid.ts.
 * ============================================================================
 *
 * WHY THIS MODULE EXISTS SEPARATELY FROM publisherApi
 * ---------------------------------------------------
 * Same split as api/lyria.ts against api/generationApi.ts, for the same reason. This
 * file is the *wire format of a third party*: its field names are LabelGrid's, its
 * enums are LabelGrid's, and its snake_case is LabelGrid's. api/publisherApi.ts is the
 * boundary the Lyrixa UI talks to, in Lyrixa's own domain language. Keeping them apart
 * means the shape of a distribution request can be built and asserted without a server,
 * while the server can also import the same contract without touching frontend runtime
 * configuration.
 *
 * VERIFICATION — READ THIS BEFORE ADDING A FIELD OR AN ENDPOINT
 * ------------------------------------------------------------
 * Every identifier, enum member, required-field list and status value below was read
 * out of the official OpenAPI document, which is committed to this repository at
 * `docs/vendor/labelgrid-openapi.json` (OpenAPI 3.1.0, title "LabelGrid API", version
 * 1.0.0, 87 paths) and was fetched from:
 *
 *     https://api.labelgrid.com/docs/api.json
 *
 * That file is the source of truth, and it is worth saying why it is committed: the
 * human-readable documentation pages at https://api.labelgrid.com/docs/api carry NO
 * field-level schemas at all. The operation pages list an endpoint and its verb and
 * nothing else. So the spec is the only place the contract exists, and a reviewer
 * checking this module against "the docs" needs the same document I read.
 *
 * THREE PLACES THE REALITY DIFFERS FROM WHAT ONE WOULD ASSUME. Each is load-bearing:
 *
 *   1. THE BASE URL IS NOT `https://api.labelgrid.com`. The spec's `servers` entry is
 *      `https://api.labelgrid.com/api/public` — the `/api/public` prefix is part of
 *      every path. A backend built against the shorter host would 404 on every call.
 *
 *   2. `POST /releases/{release}/distribute` TAKES NO REQUEST BODY. There is no
 *      outlet array, no territory list and no options object; the operation has no
 *      requestBody at all. Outlet selection is therefore *not* part of the distribute
 *      call — it is `dsp_configs[]` on the release itself, set at create or PATCH
 *      time. This is why buildReleaseRequest below carries the outlet choice and
 *      `distribute` is a bare, bodyless trigger.
 *
 *   3. COVER ART HAS TWO ENDPOINTS, not one. `POST /releases/{release}/photo` and the
 *      `POST /releases/{release}/files/{assetType}` family, where `assetType` is
 *      `square` or `tall`. The latter is the pre-signed-upload route
 *      (`…/files/{assetType}/upload-url`), which is the one a browser-originated file
 *      should travel through.
 *
 * WHAT IS *NOT* IN THE SPEC, and therefore is not in this file:
 *
 *   - THE WEBHOOK EVENT IDENTIFIERS. `GET /webhooks/event-types` returns objects with
 *     `value`, `label` and `description`. The twelve `label` and `description` strings
 *     are enumerated in the spec; the corresponding `value` identifiers are typed as a
 *     bare `string` with NO enum. So the dotted identifiers a webhook subscription
 *     actually registers are not documented statically and must be read from that
 *     endpoint at runtime. WEBHOOK_EVENT_LABELS below is the documented half; there is
 *     deliberately no invented `release.distributed`-style constant to go with it.
 *
 *   - ANY ISRC OR UPC GENERATION RULE. `isrc` is an optional field on a track and
 *     `barcode_number` an optional field on a release, whose description is "Leave
 *     blank to auto-generate". Both are therefore values LabelGrid assigns and returns;
 *     neither can be computed here, and the Publisher UI must show them only when a
 *     real response carries them.
 */

// ---------------------------------------------------------------------------
// Hosts
// ---------------------------------------------------------------------------

/**
 * LabelGrid's production base URL, including the `/api/public` prefix.
 *
 * Exported for the backend contract and for the tests that pin it — NOT for a fetch
 * from this app. Nothing in `src/` may call this host directly; transport belongs on
 * the server.
 */
export const LABELGRID_BASE_URL = 'https://api.labelgrid.com/api/public';

/**
 * The sandbox host, from LabelGrid's own documentation page.
 *
 * §38 asks that testing happen against a sandbox rather than production where one is
 * available. It is named here so the backend has the value; this build has no
 * credentials for either environment.
 */
export const LABELGRID_SANDBOX_BASE_URL = 'https://api-sandbox.stg.labelgrid.com';

/**
 * The authentication scheme, verbatim from the spec's `securitySchemes`.
 *
 * `{ type: 'http', scheme: 'bearer' }` — a bearer token on every request, including
 * the read-only ones. A single credential with full account authority, which is
 * exactly why it cannot come anywhere near this bundle.
 */
export const LABELGRID_AUTH_SCHEME = 'bearer' as const;

// ---------------------------------------------------------------------------
// Enums — every member read from the spec, none invented
// ---------------------------------------------------------------------------

/**
 * `content_type` on a release. The complete documented set.
 *
 * The Publisher UI offers Single, EP and Album, which are three of these six. That is
 * a product decision about what Lyrixa users are doing, not a limit of the API — and
 * the three the brief asks for are all genuinely supported, which is what §8's "only
 * implement release types actually supported" required checking.
 */
export const LABELGRID_CONTENT_TYPES = [
  'Single',
  'Album',
  'Compilation',
  'EP',
  'Mix',
  'Podcast',
] as const;

export type LabelGridContentType = (typeof LABELGRID_CONTENT_TYPES)[number];

/**
 * `artwork_ai_usage`, `audio_ai_usage`, `composition_ai_usage` — the AI-disclosure
 * scale, shared by all three fields (the spec's `AiUsageEnum`).
 *
 * THESE ARE MANDATORY AND THEY MATTER MORE HERE THAN ANYWHERE ELSE IN THIS FILE.
 * `audio_ai_usage` and `composition_ai_usage` are both in `POST /tracks`'s required
 * list, and every track Lyrixa produces is generated by a model. So the honest value
 * for a Lyrixa track is `'all'`, and DEFAULT_AI_USAGE_FOR_Lyrixa below states that in
 * one place rather than leaving it to a form default somebody could quietly change.
 * Declaring `'none'` on a fully AI-generated track would be a false declaration to the
 * distributor and, downstream, to the stores.
 */
export const LABELGRID_AI_USAGE = ['none', 'some', 'material', 'all'] as const;

export type LabelGridAiUsage = (typeof LABELGRID_AI_USAGE)[number];

/**
 * What Lyrixa must declare for audio and composition on a generated track.
 *
 * A named constant because it is a compliance answer, not a UI default.
 */
export const DEFAULT_AI_USAGE_FOR_Lyrixa: LabelGridAiUsage = 'all';

/** `explicit`, on both releases and tracks. */
export const LABELGRID_EXPLICIT = ['off', 'on', 'edited'] as const;

export type LabelGridExplicit = (typeof LABELGRID_EXPLICIT)[number];

/** `composition_type` on a track. */
export const LABELGRID_COMPOSITION_TYPES = [
  'original_composition',
  'cover_song',
  'public_domain',
] as const;

export type LabelGridCompositionType = (typeof LABELGRID_COMPOSITION_TYPES)[number];

/** `commercial_samples` on a track. */
export const LABELGRID_COMMERCIAL_SAMPLES = ['no', 'exclusive', 'non_exclusive'] as const;

export type LabelGridCommercialSamples = (typeof LABELGRID_COMMERCIAL_SAMPLES)[number];

/** `contributors[].ai_contribution` on a track. A different scale from AiUsageEnum. */
export const LABELGRID_AI_CONTRIBUTION = ['none', 'partly', 'all'] as const;

export type LabelGridAiContribution = (typeof LABELGRID_AI_CONTRIBUTION)[number];

/**
 * `review_status` on a release — the spec's `ReviewStatus`.
 *
 * THE PUBLISHER UI MUST NOT INVENT A MEMBER OF THIS SET. §21 and §22 are explicit
 * about not inventing provider status values, and this is the actual list. Note what
 * is *not* in it: there is no `live`, no `published` and no per-store state. Whether a
 * release reached a given outlet is a different question with a different answer — see
 * LABELGRID_DISTRO_QUEUE_STATUSES.
 */
export const LABELGRID_REVIEW_STATUSES = [
  'draft',
  'approved',
  'to_review',
  'rejected',
  'require_changes',
  'audit',
  'pending_customer_review',
] as const;

export type LabelGridReviewStatus = (typeof LABELGRID_REVIEW_STATUSES)[number];

/**
 * `delivery_status` on a release — the spec's `DeliveryStatus`.
 *
 * Four members, and the fourth is the empty string, which the spec lists literally.
 * It means "no delivery state yet" and is preserved rather than normalised away,
 * because a reader that mapped `''` to `'pending'` would be inventing a status.
 *
 * `'distributed'` means LabelGrid has sent the release onward. It does NOT mean any
 * particular store has it live. That distinction is the whole of §25.
 */
export const LABELGRID_DELIVERY_STATUSES = ['pending', 'distributed', 'takedown', ''] as const;

export type LabelGridDeliveryStatus = (typeof LABELGRID_DELIVERY_STATUSES)[number];

/**
 * Per-outlet queue status, from `GET /queues/distro`'s `status` filter enum.
 *
 * THIS IS THE ONLY DOCUMENTED SOURCE OF PER-PLATFORM STATE, and it is what §23's
 * per-platform display has to be built on. Twenty-five members, several of which are
 * ordinary rather than exceptional (`batched`, `ready-to-batch`, `transcoding`), which
 * is the reason the UI groups them rather than showing the raw string: see
 * `describeQueueStatus`.
 *
 * Note that `MinimalDistroQueueData.status` is typed as a bare string in the spec — the
 * enum lives on the query parameter. So this is the documented vocabulary, and a
 * response carrying something outside it is possible and must not crash the UI. That
 * is why `describeQueueStatus` has an `unknown` bucket instead of an exhaustive switch.
 */
export const LABELGRID_DISTRO_QUEUE_STATUSES = [
  '',
  'pending review',
  'processing',
  'scheduled',
  'transcoding',
  'on hold',
  'ok to send',
  'awaiting license',
  'paused',
  'batched',
  'batched-processing',
  'batched-init',
  'ready-to-batch',
  'missing files',
  'rejected',
  'complete',
  'error',
  'fault',
  'skipped',
  'ineligible',
  'imported',
  'batch-exception',
  'warning',
  'incomplete',
  'cancelled',
] as const;

export type LabelGridDistroQueueStatus = (typeof LABELGRID_DISTRO_QUEUE_STATUSES)[number];

/** `assetType` for release artwork uploads. */
export const LABELGRID_RELEASE_ASSET_TYPES = ['square', 'tall'] as const;

export type LabelGridReleaseAssetType = (typeof LABELGRID_RELEASE_ASSET_TYPES)[number];

/**
 * `fileType` for track file uploads.
 *
 * `stereo` is the audio master. There is no `mp3`/`wav` distinction in the path — the
 * format is a property of the file uploaded, not of the endpoint.
 */
export const LABELGRID_TRACK_FILE_TYPES = ['stereo', 'dolby', 'lyrics'] as const;

export type LabelGridTrackFileType = (typeof LABELGRID_TRACK_FILE_TYPES)[number];

/**
 * The twelve webhook event `label` values, verbatim from the spec.
 *
 * LABELS ONLY, AND THAT IS THE POINT. The `value` field each of these pairs with — the
 * identifier a subscription is actually registered against — is typed `string` with no
 * enum, so it is not documented statically. A backend registering webhooks must read
 * `GET /webhooks/event-types` and use the `value` it returns. Inventing
 * `release.distributed` because it looks plausible is precisely what §2 forbids.
 *
 * Kept here because the labels ARE documented and they tell the backend author which
 * events exist to subscribe to — which is the useful half at this stage.
 */
export const LABELGRID_WEBHOOK_EVENT_LABELS = [
  'Delivery Completed',
  'Delivery Failed',
  'Takedown Completed',
  'Release Review Status Changed',
  'Release Preflight Report Ready',
  'Release Distributed',
  'Payment Statement Ready',
  'Transcode Completed',
  'Transcode Failed',
  'Distribution Outlet Status Changed',
  'Stream Radar Flag Created',
  'Stream Radar Flag Resolved',
] as const;

export type LabelGridWebhookEventLabel = (typeof LABELGRID_WEBHOOK_EVENT_LABELS)[number];

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/**
 * The LabelGrid operations the Publisher feature needs, as documented paths.
 *
 * Every entry was checked to exist in the spec with the verb named. They are relative
 * to LABELGRID_BASE_URL and are listed so `docs/publisher-backend.md` and the backend
 * implementation cannot drift from each other — and so a test can assert that this app
 * names no endpoint the spec does not have.
 *
 * `{release}` and `{track}` are LabelGrid's own numeric ids, returned by the create
 * calls. Not Lyrixa ids.
 */
export const LABELGRID_PATHS = {
  /** GET — the outlet catalogue. The source of truth for §13's platform list. */
  distroOutlets: '/distro-outlets',
  /** GET — genre catalogue, for `primary_genre_id`. */
  genres: '/genres',
  /** GET — the authenticated account, for verifying credentials. */
  me: '/me',
  /** GET|POST — the release collection. */
  releases: '/releases',
  /** GET|PATCH|DELETE — one release. */
  release: (release: number | string) => `/releases/${release}`,
  /** POST — validate. Returns the structured errors §14 renders. */
  validate: (release: number | string) => `/releases/${release}/validate`,
  /** POST — trigger distribution. NO REQUEST BODY. */
  distribute: (release: number | string) => `/releases/${release}/distribute`,
  /** GET — the preflight/QC report. */
  qualityReport: (release: number | string) => `/releases/${release}/quality-report`,
  /** POST — pre-signed upload URL for release artwork. */
  releaseAssetUploadUrl: (release: number | string, assetType: LabelGridReleaseAssetType) =>
    `/releases/${release}/files/${assetType}/upload-url`,
  /** GET|POST — the track collection. */
  tracks: '/tracks',
  /** POST — pre-signed upload URL for a track file. */
  trackFileUploadUrl: (track: number | string, fileType: LabelGridTrackFileType) =>
    `/tracks/${track}/files/${fileType}/upload-url`,
  /** GET — per-outlet distribution queue. The per-platform status source. */
  distroQueue: '/queues/distro',
  /** GET — per-dimension royalty totals, including per-release and per-DSP gross_usd. */
  royaltiesBreakdown: '/royalties/breakdown',
  /** GET — composite platform analytics for a filtered release/date range. */
  analyticsSummary: '/analytics/summary',
  /** GET — static analytics availability/cadence matrix by platform. */
  analyticsAvailability: '/analytics/availability',
  /** GET — the webhook event vocabulary, which must be read rather than assumed. */
  webhookEventTypes: '/webhooks/event-types',
  /** GET|POST — webhook subscriptions. */
  webhooks: '/webhooks',
} as const;

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

/**
 * One outlet from `GET /distro-outlets`.
 *
 * SIX FIELDS, AND NO MORE. There is no logo, no territory list, no "supported" flag
 * and no per-outlet delivery-time estimate — so a Publisher UI that showed any of
 * those would be showing something it made up. §13's outlet picker renders `name`,
 * and groups by `type`.
 *
 * `is_ai_dsp` deserves its own note. It flags an outlet that specifically handles
 * AI-generated music, which is directly material to a product whose entire catalogue
 * is model-generated: some stores treat AI content differently, and this field is
 * LabelGrid telling us which ones. It is surfaced in the UI rather than hidden.
 */
export interface LabelGridOutlet {
  id: number;
  /** Slug, e.g. `spotify`. Accepted by `dsp_configs[].distro_outlet_id`. */
  key: string;
  /** Display name. */
  name: string;
  /** Grouping, e.g. a streaming service vs a download store. */
  type: string;
  /** True when the outlet is an AI-music DSP. */
  is_ai_dsp: boolean;
  /** True when the outlet is a user-generated-content store (TikTok, Reels …). */
  is_ugc_store: boolean;
}

/** A file reference LabelGrid returns — the spec's `FileData`. */
export interface LabelGridFileData {
  filename: string;
  url: string;
  filesize: number | null;
  status: string | null;
}

/** A genre from `GET /genres` — the spec's `GenreData`. */
export interface LabelGridGenre {
  id: number;
  name: string;
  category: string | null;
  base_genre: string | null;
}

/**
 * One structured validation problem from `POST /releases/{release}/validate`.
 *
 * `entity_type` tells the UI which step of the wizard to send the user back to, which
 * is what makes §14's "Action Required" list actionable rather than a wall of strings.
 */
export interface LabelGridValidationIssue {
  code: string;
  entity_type: 'release' | 'track' | 'artwork' | 'audio';
  entity_id: number | null;
  field: string | null;
  message: string;
}

/**
 * The `POST /releases/{release}/validate` 200 body.
 *
 * THE REAL CONTRACT FOR §14, and the reason the Publisher does not carry a
 * hand-written validation engine for LabelGrid's rules: `result` is the verdict,
 * `errors_structured` is why. The plain `errors`/`warnings` string arrays are the same
 * information without the routing metadata, and are read as a fallback.
 *
 * Warnings do NOT block submission; errors do. That asymmetry is LabelGrid's, and
 * `isSubmittable` below is the single place the app expresses it.
 */
export interface LabelGridValidationResponse {
  result: 'OK' | 'ERROR';
  errors: string[];
  errors_structured: LabelGridValidationIssue[];
  warnings: string[];
  warnings_structured: LabelGridValidationIssue[];
}

/**
 * One row of `GET /queues/distro` — the spec's `MinimalDistroQueueData`.
 *
 * This is the per-platform truth for §23: one record per (release, outlet) pair with
 * its own `status` and `updated_at`. `created_at`/`updated_at` are real ISO timestamps
 * from LabelGrid, which is what §26's "do not fabricate timestamps" needs — the UI
 * renders these or renders nothing.
 */
export interface LabelGridDistroQueueEntry {
  id: number;
  queue_config_id: number;
  distro_outlet_id: number;
  release_id: number;
  sender_id: number;
  takedown: boolean;
  thread_id: string;
  message_id: string;
  ddex_batch_id: string;
  /** Documented vocabulary in LABELGRID_DISTRO_QUEUE_STATUSES, but typed loosely. */
  status: string;
  /** ISO 8601 date-time, from LabelGrid. */
  created_at: string;
  /** ISO 8601 date-time, from LabelGrid. */
  updated_at: string;
}

/**
 * A release as LabelGrid returns it, narrowed to the fields the Publisher reads.
 *
 * DELIBERATELY A SUBSET. The full response carries ~90 fields including 28 per-store
 * URL fields, royalty plumbing and landing-page configuration, none of which this
 * feature touches. Typing only what is read keeps it honest about what the UI depends
 * on; the spec remains the reference for the rest.
 */
export interface LabelGridRelease {
  id: number;
  public_id: string;
  cat: string;
  title: string | null;
  release_date: string | null;
  /** Assigned by LabelGrid. Absent until it is. Never computed locally. */
  barcode_number: string | null;
  explicit: LabelGridExplicit;
  review_status: LabelGridReviewStatus | null;
  delivery_status: LabelGridDeliveryStatus | null;
  front_cover: LabelGridFileData | null;
  locked: boolean;
  locked_reason: string | null;
  dsp_configs: LabelGridDspConfig[];
  tracks: LabelGridReleaseTrack[] | null;
}

/** A track as it appears nested in a release response. */
export interface LabelGridReleaseTrack {
  id: number;
  track_num: number | null;
  title: string | null;
  mix_version: string | null;
  default_display_artist: string | null;
}

/** One row from `GET /royalties/breakdown`, narrowed to the platform metrics this UI reads. */
export interface LabelGridRoyaltyBreakdownRow {
  dsp?: string | null;
  release_id?: number | null;
  period?: string | null;
  currency: string;
  gross_usd: number;
  adjustment?: boolean;
}

export interface LabelGridRoyaltyBreakdownResponse {
  data: LabelGridRoyaltyBreakdownRow[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    per_page: number;
    count: number;
    cursor: string | null;
    next_cursor: string | null;
    scope: string;
    group_by: string[];
    filter: Record<string, unknown>;
    gross_definition: string;
    note: string;
    total_gross_usd: number;
    as_of: string;
    snapshot_expires_at: string;
  };
}

export type LabelGridAnalyticsAvailability = 'available' | 'not_available_for_platform';

export interface LabelGridAnalyticsSummarySectionPoint {
  date?: string | null;
  platform?: string | null;
  value?: number | null;
  streams?: number | null;
  listeners?: number | null;
  [key: string]: unknown;
}

export interface LabelGridAnalyticsSummaryResponse {
  data: Record<string, LabelGridAnalyticsSummarySectionPoint[] | unknown>;
  meta?: Record<string, unknown>;
  availability?: Record<string, LabelGridAnalyticsAvailability>;
}

export interface LabelGridAnalyticsAvailabilityResponse {
  data: {
    sections: string[];
    platforms: string[];
    availability: Record<string, Record<string, LabelGridAnalyticsAvailability>>;
    platform_cadence: Record<string, 'daily' | 'weekly'>;
  };
}

// ---------------------------------------------------------------------------
// Request shapes
// ---------------------------------------------------------------------------

/**
 * A localised text value — `titles[]`, `mix_versions[]`, `descriptions[]`.
 *
 * `iso_code` and `text` are both required by the spec. The array shape is how
 * LabelGrid carries per-language variants; a single-entry array is the normal case.
 */
export interface LabelGridLocalisedText {
  /** BCP-47 / ISO language code, e.g. `en`. */
  iso_code: string;
  text: string;
  phonetic?: string;
}

/** An artist credit on a release. Both fields are required by the spec. */
export interface LabelGridReleaseArtist {
  /** A LabelGrid artist id — from `GET|POST /artists`, not a Lyrixa value. */
  artist_id: number;
  /** e.g. `primary`, `featuring`. From LabelGrid's own vocabulary. */
  artistic_role: string;
  position?: number;
}

/**
 * One outlet's configuration on a release — `dsp_configs[]`.
 *
 * THIS IS WHERE OUTLET SELECTION LIVES, because `distribute` has no body. `enabled`
 * is the checkbox §13 renders.
 */
export interface LabelGridDspConfig {
  /** Outlet slug (`spotify`) or numeric outlet id, per the spec's own description. */
  distro_outlet_id: string;
  enabled: boolean;
  /** 0 = allow, 1 = block. UGC rights claims. */
  rights_claim_behavior?: 0 | 1;
  /** Beatport exclusivity window. */
  exclusive_period?: '2' | '4' | '8' | 'lifetime';
}

/**
 * The `POST /releases` request body.
 *
 * REQUIRED, per the spec: `content_type`, `label_id`, `artists`, `titles`, `cat`,
 * `artwork_ai_usage`, `primary_genre_id`. Everything else is optional and is included
 * here only where the Publisher actually collects it — §10 is explicit that metadata
 * should not be collected merely because it looks good in a form.
 *
 * THREE OF THE SEVEN REQUIRED FIELDS ARE LABELGRID ACCOUNT RESOURCES: `label_id`,
 * `artists[].artist_id` and `primary_genre_id` are all foreign keys into the
 * LabelGrid account. They cannot be derived from a Lyrixa song, a Lyrixa user or
 * anything in this repository — they have to be created or looked up through
 * `/labels`, `/artists` and `/genres` against real credentials. This is the single
 * biggest reason the integration cannot be completed client-side, and it is reported
 * as such rather than filled with plausible integers.
 */
export interface LabelGridReleaseRequest {
  content_type: LabelGridContentType;
  label_id: number;
  artists: LabelGridReleaseArtist[];
  titles: LabelGridLocalisedText[];
  /** Catalogue number. Required, and the label's own identifier for the release. */
  cat: string;
  artwork_ai_usage: LabelGridAiUsage;
  primary_genre_id: number;
  secondary_genre_id?: number;
  /** ISO date. Optional — LabelGrid schedules on its own terms when absent. */
  release_date?: string;
  explicit?: LabelGridExplicit;
  /** Optional. "Leave blank to auto-generate" — so it is omitted, never invented. */
  barcode_number?: string;
  /** Where outlet selection travels. See LabelGridDspConfig. */
  dsp_configs?: LabelGridDspConfig[];
  preferred_localization?: string;
}

/** A contributor on a track. `roles` and `ai_contribution` are required. */
export interface LabelGridTrackContributor {
  /** LabelGrid contributor roles — from `GET /contributor-roles`. */
  roles: string[];
  ai_contribution: LabelGridAiContribution;
  /** A LabelGrid writer id, where the contributor is a writer. */
  writer_id?: number;
  instrument_type?: string;
}

/**
 * The `POST /tracks` request body.
 *
 * REQUIRED, per the spec: `release_id`, `disc`, `track_num`, `composition_type`,
 * `artists`, `audio_ai_usage`, `composition_ai_usage`, `commercial_samples`,
 * `audio_language`, `contributors`.
 *
 * Note how much of that is disclosure rather than description. A distributor is asking,
 * as a condition of accepting the track, how it was made — and for Lyrixa the answer
 * is "by a model". See DEFAULT_AI_USAGE_FOR_Lyrixa.
 */
export interface LabelGridTrackRequest {
  release_id: number;
  disc: number;
  track_num: number;
  composition_type: LabelGridCompositionType;
  artists: LabelGridReleaseArtist[];
  audio_ai_usage: LabelGridAiUsage;
  composition_ai_usage: LabelGridAiUsage;
  commercial_samples: LabelGridCommercialSamples;
  /** BCP-47. `zxx` for an instrumental, per the spec's own note. */
  audio_language: string;
  contributors: LabelGridTrackContributor[];
  titles?: LabelGridLocalisedText[];
  explicit?: LabelGridExplicit;
  /** Optional, and LabelGrid-assigned when absent. Never fabricated here. */
  isrc?: string;
  primary_genre_id?: number;
}

/** Thrown when a caller asks for a request LabelGrid could not accept. */
export class LabelGridRequestError extends Error {
  readonly name = 'LabelGridRequestError';
}

/**
 * Builds the documented `POST /releases` body.
 *
 * Refuses locally what the API would refuse remotely, so the message a caller sees is
 * written by whoever knew the rule — the same argument buildLyriaRequest makes. Note
 * that it validates *presence and shape*, not LabelGrid's business rules: those belong
 * to `POST /releases/{release}/validate`, and duplicating them here is how a bespoke
 * validation engine gets built by accident.
 *
 * `audio_language` has no equivalent here because language is a track-level field.
 *
 * @throws LabelGridRequestError when a required field is missing or empty.
 */
export function buildReleaseRequest(input: {
  contentType: LabelGridContentType;
  labelId: number;
  artists: LabelGridReleaseArtist[];
  title: string;
  /** Language of `title`. BCP-47. */
  titleLanguage?: string;
  cat: string;
  artworkAiUsage: LabelGridAiUsage;
  primaryGenreId: number;
  releaseDate?: string;
  explicit?: LabelGridExplicit;
  outlets?: readonly string[];
}): LabelGridReleaseRequest {
  const title = input.title.trim();
  if (!title) {
    throw new LabelGridRequestError('A release needs a title.');
  }
  const cat = input.cat.trim();
  if (!cat) {
    throw new LabelGridRequestError('A release needs a catalogue number (`cat`).');
  }
  if (input.artists.length === 0) {
    throw new LabelGridRequestError('A release needs at least one artist credit.');
  }
  if (!Number.isInteger(input.labelId) || input.labelId <= 0) {
    throw new LabelGridRequestError(
      'A release needs a LabelGrid `label_id`. Labels are created through /labels against ' +
        'real credentials and cannot be derived from Lyrixa data.'
    );
  }
  if (!Number.isInteger(input.primaryGenreId) || input.primaryGenreId <= 0) {
    throw new LabelGridRequestError(
      'A release needs a LabelGrid `primary_genre_id`, read from GET /genres.'
    );
  }

  const request: LabelGridReleaseRequest = {
    content_type: input.contentType,
    label_id: input.labelId,
    artists: input.artists,
    titles: [{ iso_code: input.titleLanguage ?? 'en', text: title }],
    cat,
    artwork_ai_usage: input.artworkAiUsage,
    primary_genre_id: input.primaryGenreId,
  };

  if (input.releaseDate) request.release_date = input.releaseDate;
  if (input.explicit) request.explicit = input.explicit;
  // Only when outlets were chosen. An empty dsp_configs array is a different
  // statement from an absent one — it says "no outlets", which would be a release
  // distributed nowhere.
  if (input.outlets && input.outlets.length > 0) {
    request.dsp_configs = input.outlets.map((key) => ({ distro_outlet_id: key, enabled: true }));
  }

  // `barcode_number` is deliberately never set: the spec says leave it blank to
  // auto-generate, and a UPC this app made up would be a UPC that collides with a real
  // one. Same for `isrc` on the track builder below.
  return request;
}

/**
 * Builds the documented `POST /tracks` body for a Lyrixa-generated track.
 *
 * The AI-disclosure fields default to the truthful answer for this product rather than
 * to the API's most permissive value. A caller may override them — a track with
 * human-written lyrics is genuinely `composition_ai_usage: 'some'` — but the default
 * cannot be `'none'` for audio a model produced.
 *
 * @throws LabelGridRequestError on a missing release id or an out-of-range track number.
 */
export function buildTrackRequest(input: {
  releaseId: number;
  trackNum: number;
  disc?: number;
  artists: LabelGridReleaseArtist[];
  title?: string;
  titleLanguage?: string;
  /** BCP-47, or `zxx` for an instrumental. */
  audioLanguage: string;
  compositionType?: LabelGridCompositionType;
  audioAiUsage?: LabelGridAiUsage;
  compositionAiUsage?: LabelGridAiUsage;
  commercialSamples?: LabelGridCommercialSamples;
  contributors?: LabelGridTrackContributor[];
  explicit?: LabelGridExplicit;
}): LabelGridTrackRequest {
  if (!Number.isInteger(input.releaseId) || input.releaseId <= 0) {
    throw new LabelGridRequestError('A track needs the LabelGrid `release_id` it belongs to.');
  }
  if (!Number.isInteger(input.trackNum) || input.trackNum < 1) {
    throw new LabelGridRequestError('`track_num` starts at 1.');
  }
  if (input.artists.length === 0) {
    throw new LabelGridRequestError('A track needs at least one artist credit.');
  }
  if (!input.audioLanguage.trim()) {
    throw new LabelGridRequestError(
      'A track needs `audio_language`. Use `zxx` for a track with no sung language.'
    );
  }

  const request: LabelGridTrackRequest = {
    release_id: input.releaseId,
    disc: input.disc ?? 1,
    track_num: input.trackNum,
    composition_type: input.compositionType ?? 'original_composition',
    artists: input.artists,
    audio_ai_usage: input.audioAiUsage ?? DEFAULT_AI_USAGE_FOR_Lyrixa,
    composition_ai_usage: input.compositionAiUsage ?? DEFAULT_AI_USAGE_FOR_Lyrixa,
    // `no`, because Lyrixa generates rather than samples. Not a guess: the alternatives
    // both assert a sample licence exists, which would be a false declaration.
    commercial_samples: input.commercialSamples ?? 'no',
    audio_language: input.audioLanguage.trim(),
    /*
      Required, and the honest minimum for a generated track: one contributor whose
      contribution is entirely AI. An empty array would fail the API's own requirement,
      and inventing a named human writer would be worse than failing.
    */
    contributors: input.contributors ?? [{ roles: ['composer'], ai_contribution: 'all' }],
  };

  const title = input.title?.trim();
  if (title) request.titles = [{ iso_code: input.titleLanguage ?? 'en', text: title }];
  if (input.explicit) request.explicit = input.explicit;

  return request;
}

// ---------------------------------------------------------------------------
// Reading responses
// ---------------------------------------------------------------------------

/**
 * Whether a validation result permits submission.
 *
 * ONE PLACE, because §14 requires that submission be blocked while required issues
 * remain and it would be easy for a second copy of this rule to disagree. Errors block;
 * warnings do not. `result` is trusted as the verdict, with the error array as a
 * belt-and-braces check for a response that says OK while listing errors.
 */
export function isSubmittable(validation: LabelGridValidationResponse): boolean {
  return (
    validation.result === 'OK' &&
    validation.errors_structured.length === 0 &&
    validation.errors.length === 0
  );
}

/**
 * Every blocking issue, as one list the UI can render.
 *
 * Prefers `errors_structured` because it carries the routing metadata, and falls back
 * to the plain `errors` strings so an issue is never silently dropped just because the
 * structured array was empty — the user would then see "cannot submit" with nothing to
 * act on, which is the worst of both.
 */
export function readValidationErrors(
  validation: LabelGridValidationResponse
): LabelGridValidationIssue[] {
  if (validation.errors_structured.length > 0) return validation.errors_structured;
  return validation.errors.map((message) => ({
    code: 'unstructured',
    entity_type: 'release' as const,
    entity_id: null,
    field: null,
    message,
  }));
}

/** Non-blocking advisories, in the same shape. */
export function readValidationWarnings(
  validation: LabelGridValidationResponse
): LabelGridValidationIssue[] {
  if (validation.warnings_structured.length > 0) return validation.warnings_structured;
  return validation.warnings.map((message) => ({
    code: 'unstructured',
    entity_type: 'release' as const,
    entity_id: null,
    field: null,
    message,
  }));
}

/**
 * How a per-outlet queue status should be presented.
 *
 * WHY A BUCKET AND NOT THE RAW STRING. There are 25 documented statuses and a user
 * does not need to know the difference between `batched-init` and `ready-to-batch` —
 * both mean "on its way". What they do need is whether something is wrong. So the
 * mapping is: `delivered` (LabelGrid confirms the outlet has it), `failed`, `blocked`
 * (needs a human), `pending` (in flight) and `unknown`.
 *
 * `unknown` exists because `MinimalDistroQueueData.status` is a bare string in the
 * spec: LabelGrid may return a value outside the documented filter enum, and the honest
 * response to that is to show the raw value as-is rather than to guess which bucket it
 * belongs in or to crash.
 *
 * NOTHING HERE MAPS TO "LIVE ON SPOTIFY". `complete` means the delivery finished, which
 * is the strongest claim LabelGrid's own data supports — see §25 and the note on
 * `describeDeliveryClaim`.
 */
export type QueueStatusKind = 'delivered' | 'failed' | 'blocked' | 'pending' | 'unknown';

export function describeQueueStatus(status: string): QueueStatusKind {
  switch (status) {
    case 'complete':
      return 'delivered';
    case 'error':
    case 'fault':
    case 'rejected':
    case 'batch-exception':
      return 'failed';
    case 'on hold':
    case 'missing files':
    case 'awaiting license':
    case 'paused':
    case 'ineligible':
    case 'cancelled':
    case 'skipped':
    case 'incomplete':
    case 'warning':
      return 'blocked';
    case '':
    case 'pending review':
    case 'processing':
    case 'scheduled':
    case 'transcoding':
    case 'ok to send':
    case 'batched':
    case 'batched-processing':
    case 'batched-init':
    case 'ready-to-batch':
    case 'imported':
      return 'pending';
    default:
      return 'unknown';
  }
}

/**
 * What may honestly be said about an outlet, given its queue status.
 *
 * §25 IS THE REASON THIS FUNCTION EXISTS: "Do NOT tell the user 'Your song is now live
 * on Spotify' unless the distribution system actually confirms it." LabelGrid confirms
 * *delivery*, which is not the same event as a store publishing the track — stores take
 * their own time after receiving a delivery, and LabelGrid has no field that says
 * "live". So the strongest supported wording is "delivered to", and this function is
 * the single place the app's copy is decided.
 */
export function describeDeliveryClaim(status: string): string {
  switch (describeQueueStatus(status)) {
    case 'delivered':
      return 'Delivered — the outlet has received this release.';
    case 'failed':
      return 'Delivery failed.';
    case 'blocked':
      return 'Held — this outlet needs something resolved before delivery.';
    case 'pending':
      return 'In progress — not yet delivered.';
    default:
      return 'Status reported by LabelGrid, not recognised by this build.';
  }
}
